import Keycloak from "keycloak-js";
import { KeycloakUser } from "./Auth.d";
import UserService, { invalidOtpString, otpRequiredString } from "./User";
import { User } from "./User.d";

export class AuthService {
  API_URL: string | undefined = process.env.REACT_APP_KEYCLOAK_URL;
  USER_KEY: string | undefined = process.env.REACT_APP_USER_KEY;
  REFRESH_KEY: string | undefined = process.env.REACT_APP_REFRESH_KEY;
  realm: string | undefined = process.env.REACT_APP_REALM;
  client_id: string | undefined = process.env.REACT_APP_CLIENT_ID;
  redirect_uri: string | undefined = process.env.REACT_APP_REDIRECT_URI;

  keycloak: Keycloak | null;

  constructor(
    API_URL?: string,
    USER_KEY?: string,
    REFRESH_KEY?: string,
    client_id?: string,
    realm?: string,
    redirect_uri?: string
  ) {
    this.keycloak = this.initKeycloak();

    if (REFRESH_KEY) {
      this.REFRESH_KEY = REFRESH_KEY;
    }
    if (USER_KEY) {
      this.USER_KEY = USER_KEY;
    }
    if (API_URL) {
      this.API_URL = API_URL;
    }
    if (realm) {
      this.realm = realm;
    }
    if (client_id) {
      this.client_id = client_id;
    }
    if (redirect_uri) {
      this.redirect_uri = redirect_uri;
    }
  }
  userService = new UserService();
  initKeycloak(): Keycloak | null {
    if (this.API_URL && this.realm && this.client_id) {
      const keycloakOptions = {
        url: this.API_URL,
        realm: this.realm,
        clientId: this.client_id,
      };

      const keycloak_instance = new Keycloak(keycloakOptions);

      keycloak_instance.onAuthSuccess = () => {
        if (this.keycloak != null && this.keycloak.token) {
          this.setAccessToken(this.keycloak.token);
          if (this.keycloak.refreshToken) {
            this.setRefreshToken(this.keycloak.refreshToken);
          }
          if (this.redirect_uri) {
            window.location.replace(this.redirect_uri);
          }
        }
      };

      keycloak_instance.onAuthError = (error) => {
        console.log(error);
      };

      keycloak_instance
        .init({})
        .then(() => {})
        .catch(() => {
          console.log("Failed to initialize Keycloak");
          return null;
        });
      return keycloak_instance;
    } else {
      console.log("Missing Keycloak options");
      return null;
    }
  }
  login(code: string): Promise<void> {
    const tokenUrl = `${this.API_URL}/realms/${this.realm}/protocol/openid-connect/token`;
    // return a promise to handle the login
    return new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", tokenUrl, true);
      xhr.setRequestHeader("Content-type", "application/x-www-form-urlencoded");
      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
          if (xhr.status === 200) {
            let data;
            try {
              data = JSON.parse(xhr.responseText);
            } catch (err) {
              data = xhr.responseText;
            }

            // print all the data to the console
            console.log("[Auth Service] Response data:", data);

            // if the tokens were retrieved successfully
            if (data?.access_token && data?.refresh_token) {
              console.log("[Auth Service] Tokens retrieved successfully");
              this.setAccessToken(data.access_token);
              this.setRefreshToken(data.refresh_token);

              resolve();
            } else {
              reject();
            }
          } else {
            console.error(
              "[Auth Service] Unable to retrieve tokens: " + xhr.statusText
            );
            reject();
          }
        }
      };
      if (this.client_id && this.redirect_uri) {
        const params = new URLSearchParams({
          grant_type: "authorization_code",
          client_id: this.client_id,
          redirect_uri: this.redirect_uri,
          code, // code from the request from the client
        });

        xhr.send(params.toString());
      }
    });
  }

  async refresh(): Promise<boolean> {
    return new Promise((resolve) => {
      const req = new XMLHttpRequest();
      req.open(
        "POST",
        this.API_URL +
          "/realms/" +
          this.realm +
          "/protocol/openid-connect/token",
        true
      );
      req.setRequestHeader("Content-type", "application/x-www-form-urlencoded");
      req.withCredentials = true;
      const refresh_token = this.getRefreshToken();
      let request_body;
      if (this.client_id) {
        request_body =
          "grant_type=refresh_token&refresh_token=" +
          refresh_token +
          "&client_id=" +
          encodeURIComponent(this.client_id);
      }

      req.onload = () => {
        if (req.status === 200) {
          const response = JSON.parse(req.responseText);
          this.setAccessToken(response["access_token"]);
          this.setRefreshToken(response["refresh_token"]);
          resolve(true);
        } else {
          console.log("REFRESH FAILED");
          console.log(req.status);
          console.log(req.responseText);
          resolve(false);
        }
      };

      req.onerror = () => {
        resolve(false);
      };

      req.send(request_body);
    });
  }

  logout(): Promise<void> {
    return new Promise<void>((resolve) => {
      const req = new XMLHttpRequest();
      req.open(
        "POST",
        this.API_URL +
          "/realms/" +
          this.realm +
          "/protocol/openid-connect/logout?redirect_uri=" +
          window.location.origin,
        true
      );
      req.setRequestHeader("Content-type", "application/x-www-form-urlencoded");
      req.withCredentials = true;
      const refresh_token = this.getRefreshToken();
      let request_body;
      if (this.client_id) {
        request_body =
          "client_id=" +
          encodeURIComponent(this.client_id) +
          "&refresh_token=" +
          refresh_token;
      }

      req.onreadystatechange = () => {
        if (req.readyState === 4) {
          // If the user's refresh token is active, this will return a status code of 0. If it is not active, this will return a 400.
          // Either way we want to remove their tokens from storage.
          if (this.USER_KEY && this.REFRESH_KEY) {
            window.sessionStorage.removeItem(this.USER_KEY);
          }
          if (this.REFRESH_KEY) {
            window.sessionStorage.removeItem(this.REFRESH_KEY);
          }
          this.clearLocalStorageAndCookies();
          resolve();
        }
      };

      req.onerror = () => {
        // Clean up tokens even if request fails
        if (this.USER_KEY && this.REFRESH_KEY) {
          window.sessionStorage.removeItem(this.USER_KEY);
        }
        if (this.REFRESH_KEY) {
          window.sessionStorage.removeItem(this.REFRESH_KEY);
        }
        this.clearLocalStorageAndCookies();
        resolve();
      };

      req.send(request_body);
    });
  }

  async validate_session() {
    try {
      const refresh = await this.refresh();
      if (!refresh) this.logout();
      return refresh;
    } catch (e) {
      this.logout();
      console.error("Session validation failed", e);
      return false;
    }
  }

  public clearLocalStorageAndCookies() {
    // only clear the local storage associated with the user
    if (this.USER_KEY) {
      window.sessionStorage.removeItem(this.USER_KEY);
    }
    if (this.REFRESH_KEY) {
      window.sessionStorage.removeItem(this.REFRESH_KEY);
    }
    this.clearKeycloakCookies();
  }

  public clearKeycloakCookies() {
    const cookies = document.cookie.split(";");

    cookies.forEach((cookie) => {
      const cookieName = cookie.split("=")[0].trim();
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${this.API_URL}`;
    });
  }

  async loadUser(token, userOtp): Promise<User | string | null> {
    let req = new XMLHttpRequest();
    return new Promise((resolve) => {
      req.open(
        "GET",
        this.API_URL +
          "/realms/" +
          this.realm +
          "/protocol/openid-connect/userinfo",
        true
      );
      req.setRequestHeader("Content-type", "application/x-www-form-urlencoded");
      req.setRequestHeader("Authorization", "Bearer " + token);
      req.onload = () => {
        if (req.status === 200) {
          const kUser: KeycloakUser = JSON.parse(req.response);
          this.userService
            .getUserGroupID(kUser.preferred_username, userOtp)
            .then((res) => {
              if (res === otpRequiredString) {
                resolve(otpRequiredString);
                return;
              }
              if (res === invalidOtpString) {
                resolve(invalidOtpString);
                return;
              }

              this.userService
                .getUserInfo(kUser.preferred_username, res, userOtp)
                .then((r) => {
                  resolve(r);
                })
                .catch((r) => {
                  console.log("Failed to retrieve user information", r);
                  resolve(r);
                });
            })
            .catch((r) => {
              console.log("Failed to retrieve user group ID", r);
              resolve(r);
            });
        } else {
          resolve(null);
        }
      };

      req.send();
    });
  }

  getCurrentUser(): string | null {
    const userStr = this.getAccessToken();
    if (userStr !== "" && userStr !== null) {
      return userStr;
    } else {
      return null;
    }
  }

  private getAccessToken(): string | null {
    if (this.USER_KEY) {
      return window.sessionStorage.getItem(this.USER_KEY);
    }
    console.log("failed to get token: USER_KEY missing");
    return null;
  }

  private getRefreshToken(): string | null {
    if (this.REFRESH_KEY) {
      return window.sessionStorage.getItem(this.REFRESH_KEY);
    }
    console.log("failed to get token: REFRESH_KEY missing");
    return null;
  }

  private setAccessToken(accessToken: string): void {
    if (this.USER_KEY) {
      window.sessionStorage.setItem(this.USER_KEY, accessToken);
    } else {
      console.log("failed to set token: USER_KEY is missing");
    }
  }

  private setRefreshToken(refreshToken: string): void {
    if (this.REFRESH_KEY) {
      window.sessionStorage.setItem(this.REFRESH_KEY, refreshToken);
    } else {
      console.log("failed to set token: REFRESH_KEY is missing");
    }
  }
}

export default new AuthService();
